import type { IdProperties, RegisteredId } from '@luminovo/commons';
import { createContext, type PropsWithChildren, type ReactElement, useContext } from 'react';

import type { AddressableSlotAssignment } from '../api/fetchSlotAssignments';

/**
 * Sends one analytics event. Registry-typed, so a call site cannot invent an event
 * name or send the wrong properties for one.
 */
export type SlotAnalyticsTrack = <T extends RegisteredId>(event: T, properties: IdProperties<T>) => void;

// A no-op, which is also the correct default rather than merely a safe one.
//
// This package renders widgets; it does not decide whether anybody is being
// measured. The host holds that decision — in lumiquote-frontend, Segment is only
// initialized once four separate gates pass (production, the ITAR
// DISABLE_THIRD_PARTY_TRACKING switch, the analytics cookie consent, and a
// non-Luminovo email). A slot rendered without a provider is a host that has not
// opted into measurement, which is not an error state.
const noop: SlotAnalyticsTrack = () => {};

const SlotAnalyticsContext = createContext<SlotAnalyticsTrack>(noop);

export interface SlotAnalyticsProviderProps extends PropsWithChildren {
    track: SlotAnalyticsTrack;
}

/**
 * Supplies the analytics sink every slot below it reports to.
 *
 * Injected rather than imported so this package stays host-agnostic: the sink lives
 * in `@luminovo/data-stack` alongside Segment, Sentry, and the rest of the
 * product's third-party surface, and a slot primitive has no business depending on
 * any of it. Injection also means a test asserts against a spy instead of mocking
 * a module.
 */
export function SlotAnalyticsProvider({ track, children }: SlotAnalyticsProviderProps): ReactElement {
    return <SlotAnalyticsContext.Provider value={track}>{children}</SlotAnalyticsContext.Provider>;
}

export function useSlotAnalytics(): SlotAnalyticsTrack {
    return useContext(SlotAnalyticsContext);
}

// Apps whose name carries this prefix are staff-authored and served to EVERY
// tenant (lumi-mcp-server's UNIVERSAL_APP_PREFIX; the prefix IS the routing rule,
// reserved at authoring time, so it cannot collide with a tenant's own app).
//
// Read here rather than taken from the assignments response because the response
// carries no such flag. That does duplicate a server-side rule in the frontend,
// which is worth knowing: the cost of being wrong is one mislabelled analytics
// dimension, and the alternative was widening a wire contract for a metric.
const UNIVERSAL_APP_PREFIX = 'lumi-';

/**
 * The app/widget/universal dimensions every widget event carries, so the four call
 * sites in SlotContainer cannot drift from each other.
 */
export function widgetAttribution(
    assignment: Pick<AddressableSlotAssignment, 'app' | 'widget'>,
    slotId: string,
): { app: string; widget: string; slot: string; universal: boolean } {
    return {
        app: assignment.app.id,
        widget: assignment.widget.id,
        slot: slotId,
        universal: assignment.app.id.startsWith(UNIVERSAL_APP_PREFIX),
    };
}
